import { F } from '../level/types';
import type { World } from './world';

export class Enemy {
  w = 14;
  h = 15;
  vx: number;
  vy = 0;
  alive = true;
  deadT = 0;
  onGround = false;
  phase = Math.random() * 10;

  constructor(
    public x: number,
    public y: number,
    readonly kind: 'cn' | 'vandal',
    dir: number,
  ) {
    this.vx = dir * (kind === 'cn' ? 32 : 54);
  }

  step(dt: number, world: World): void {
    if (!this.alive) {
      this.deadT += dt;
      return;
    }
    this.phase += dt;
    this.vy = Math.min(this.vy + 2200 * dt, 650);
    // Horizontal: turn at walls and before walking off an edge.
    const nx = this.x + this.vx * dt;
    if (world.solidRect(nx, this.y, this.w, this.h - 1)) this.vx = -this.vx;
    else {
      const ahead = this.vx > 0 ? nx + this.w + 1 : nx - 1;
      if (this.onGround && !world.floorAt(ahead, this.y + this.h + 2)) this.vx = -this.vx;
      else this.x = nx;
    }
    const prevBottom = this.y + this.h;
    this.y += this.vy * dt;
    this.onGround = false;
    for (const t of world.query(this.x, this.y, this.x + this.w, this.y + this.h, [])) {
      const solid = world.solid(t);
      const oneway = !solid && world.oneway(t) && prevBottom <= t.y + 0.5;
      if (!(solid || oneway) || this.vy < 0) continue;
      if (t.flags & F.HAZARD) continue;
      this.y = t.y - this.h;
      this.vy = 0;
      this.onGround = true;
    }
  }
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
  kind: 'dot' | 'spark' | 'text' | 'ring' | 'flame';
  text?: string;
  gravity?: number;
}

export class Particles {
  list: Particle[] = [];

  add(p: Omit<Particle, 'max'> & { max?: number }): void {
    if (this.list.length > 600) this.list.shift();
    this.list.push({ ...p, max: p.max ?? p.life });
  }

  burst(x: number, y: number, n: number, color: string, speed = 120, kind: Particle['kind'] = 'dot', gravity = 400): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.4 + Math.random() * 0.8);
      this.add({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s - speed * 0.3,
        life: 0.4 + Math.random() * 0.4,
        size: 1.5 + Math.random() * 2,
        color,
        kind,
        gravity,
      });
    }
  }

  dust(x: number, y: number, n = 6): void {
    for (let i = 0; i < n; i++)
      this.add({
        x: x + (Math.random() - 0.5) * 10,
        y,
        vx: (Math.random() - 0.5) * 90,
        vy: -Math.random() * 40,
        life: 0.3 + Math.random() * 0.25,
        size: 1.5 + Math.random() * 2,
        color: 'rgba(120,125,135,0.55)',
        kind: 'dot',
        gravity: 60,
      });
  }

  text(x: number, y: number, text: string, color = '#202122'): void {
    this.add({ x, y, vx: 0, vy: -42, life: 1.1, size: 12, color, kind: 'text', text, gravity: 0 });
  }

  step(dt: number): void {
    const l = this.list;
    let j = 0;
    for (let i = 0; i < l.length; i++) {
      const p = l[i];
      p.life -= dt;
      if (p.life <= 0) continue;
      p.vy += (p.gravity ?? 0) * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      l[j++] = p;
    }
    l.length = j;
  }
}
